'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { resolveProductButtonHref } from '@/lib/product-urls';
import { ImageType, ProductInnerButton } from '@/types/product';

interface FAQ {
  id: number;
  faqTitle: string;
  faqContent: string;
}

interface ProductStandaloneFaqSectionProps {
  locale: string;
  heading?: string;
  subheading?: string | null;
  faqs?: FAQ[];
  media?: ImageType;
  helpText?: string;
  connectButton?: ProductInnerButton;
  showHeader?: boolean;
}

export default function ProductStandaloneFaqSection({
  locale,
  heading = 'Frequently Asked Questions',
  subheading,
  faqs = [],
  media,
  helpText,
  connectButton,
  showHeader = true,
}: ProductStandaloneFaqSectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [openIndex, setOpenIndex] = useState(0);

  const displayFaqs = useMemo(
    () =>
      faqs.map((faq) => ({
        id: faq.id,
        question: faq.faqTitle,
        answer: faq.faqContent,
      })),
    [faqs],
  );

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section || displayFaqs.length === 0) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const headingEls = gsap.utils.toArray<HTMLElement>('[data-product-faq-heading]');
      const faqItems = gsap.utils.toArray<HTMLElement>('[data-product-faq-item]');
      const visual = section.querySelector<HTMLElement>('[data-product-faq-visual]');
      const panel = section.querySelector<HTMLElement>('[data-product-faq-panel]');
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        gsap.set([visual, panel, ...headingEls, ...faqItems].filter(Boolean), {
          clearProps: 'all',
        });
        return;
      }

      gsap.set(visual, { autoAlpha: 0, scale: 1.06 });
      gsap.set(panel, { autoAlpha: 0, x: 42, scale: 0.98 });
      gsap.set(headingEls, { autoAlpha: 0, y: 28 });
      gsap.set(faqItems, { autoAlpha: 0, y: 34 });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 78%',
            once: true,
          },
        })
        .to(visual, {
          autoAlpha: 1,
          scale: 1,
          duration: 1.15,
          ease: 'power3.out',
        })
        .to(
          panel,
          {
            autoAlpha: 1,
            x: 0,
            scale: 1,
            duration: 0.9,
            ease: 'power3.out',
          },
          '<0.12',
        )
        .to(
          headingEls,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.08,
            ease: 'power3.out',
          },
          '<0.1',
        )
        .to(
          faqItems,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.65,
            stagger: 0.08,
            ease: 'power3.out',
          },
          '<0.2',
        );
    }, section);

    return () => ctx.revert();
  }, [displayFaqs.length]);

  if (displayFaqs.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      data-page-reveal="false"
      id="product-faqs"
      className={`relative isolate overflow-hidden text-[#0c0c0b] ${
        showHeader ? 'py-10 sm:py-12 md:py-14' : 'py-0'
      }`}
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,0.9fr)] lg:gap-10">
          <div
            data-product-faq-visual
            className="relative overflow-hidden bg-[#f8f7f4] rounded-3xl"
          >
            {media ? (
              <ImageWithFallback
                image={media}
                alt={media.alternativeText || 'Product FAQ image'}
                width={media.width || 1200}
                height={media.height || 900}
                priority={false}
                sizes="(max-width: 1024px) 100vw, 46vw"
                className="h-auto w-full object-contain"
              />
            ) : (
              <CmsImage
                src={MEDIA_PLACEHOLDER}
                alt="Product FAQ image"
                width={1200}
                height={900}
                priority={false}
                sizes="(max-width: 1024px) 100vw, 46vw"
                className="h-auto w-full object-contain"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            )}
            <div
              aria-hidden
              className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_42%,rgba(12,12,11,0.16)_100%)]"
            />
          </div>

          <div
            data-product-faq-panel
            className="relative w-full lg:max-w-[560px] lg:justify-self-end"
          >
            {showHeader && (
              <>
                <h2
                  data-product-faq-heading
                  className="text-3xl font-semibold tracking-tight text-[#0c0c0b] md:text-4xl lg:text-[2.5rem] lg:leading-tight"
                >
                  {heading}
                </h2>
                {subheading && (
                  <div
                    data-product-faq-heading
                    className="prose prose-gray mt-3 max-w-lg text-sm leading-relaxed text-[#0c0c0b]/65 sm:text-base"
                    dangerouslySetInnerHTML={{ __html: subheading }}
                  />
                )}
              </>
            )}

            <div
              className={`${showHeader ? 'mt-5' : ''} divide-y divide-[#0c0c0b]/10 border-y border-[#0c0c0b]/10`}
            >
              {displayFaqs.map((faq, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={faq.id}
                    id={`faq-${faq.id}`}
                    data-product-faq-item
                    className={'group scroll-mt-28 transition-colors duration-300'}
                  >
                    <button
                      type="button"
                      className="flex w-full items-start justify-between gap-5 px-3 py-4 text-left sm:px-4"
                      aria-expanded={isOpen}
                      aria-controls={`product-faq-panel-${faq.id}`}
                      onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    >
                      <span className="text-[15px] font-medium leading-snug tracking-tight text-[#0c0c0b] sm:text-base">
                        {faq.question}
                      </span>
                      <span
                        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[#0c0c0b]/70 transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                          isOpen ? 'rotate-180 bg-[#f1f1f1]' : 'bg-white'
                        }`}
                      >
                        <ChevronDown className="h-4 w-4" aria-hidden />
                      </span>
                    </button>

                    <div
                      id={`product-faq-panel-${faq.id}`}
                      className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div
                          className={`prose prose-sm max-w-none px-3 pb-4 pr-10 text-sm leading-relaxed text-[#0c0c0b]/60 transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] sm:px-4 [&_p]:my-0 ${
                            isOpen ? 'translate-y-0' : '-translate-y-2'
                          }`}
                          dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              data-product-faq-heading
              className="mt-5 flex flex-col items-start gap-3 pt-1 sm:flex-row sm:items-center sm:justify-end"
            >
              {helpText ? <span className="text-sm text-[#0c0c0b]/58">{helpText}</span> : null}
              {connectButton ? (
                <SwapTextButton
                  href={resolveProductButtonHref(connectButton.buttonUrl, locale)}
                  {...getCmsButtonLinkProps(connectButton)}
                  text={connectButton.buttonText}
                  showArrow={connectButton.iconAlignment === 'Right'}
                  aria-label={connectButton.ariaLabel ?? connectButton.buttonText}
                  className="min-h-11 rounded-md px-5 py-3 shadow-none"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
